import React, { useEffect, useRef, useState } from 'react';
import { Alert, Avatar, Button, Tag } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { fetchPostComments, getPostErrorMessage } from '@/apis/posts';
import './CommentThreads.scss';
import ReportButton from '@/components/reportbutton';
import UserProfileLink from '@/components/userprofilelink';
import { applicationColors } from '@/utils/applications';

export const commentPage = result => {
  if (Array.isArray(result)) return { data: result, pagination: { page: 1, totalPages: 1 } };
  if (!Array.isArray(result?.data) || !result.pagination) throw new Error('评论列表响应无效');
  return result;
};
const REPLY_BATCH_SIZE = 10;
const mergeComments = (current, incoming) => [...new Map([...current, ...incoming].map(item => [String(item.id), item])).values()];
const sortReplies = replies => [...replies].sort((a, b) => {
  const time = (Date.parse(a.created_at) || 0) - (Date.parse(b.created_at) || 0);
  return time || Number(a.id) - Number(b.id);
});
const applicationLabels = { pending: '申请待审批', approved: '申请已同意', rejected: '申请已拒绝', withdrawn: '申请已撤回' };

const CommentRow = ({ comment, parentComment, onReply, targetId, showRecruitment, renderReply, children }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (String(targetId) === String(comment.id)) ref.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  }, [targetId, comment.id]);
  return <article className={`post-detail-comment ${String(targetId) === String(comment.id) ? 'commentThreads-target' : ''}`} ref={ref} data-comment-id={comment.id}>
    <UserProfileLink user={comment.user}><Avatar src={comment.user?.avatar || undefined} icon={<UserOutlined />} /></UserProfileLink>
    <div className="commentThreads-body">
      <div className="post-detail-comment-heading"><strong>{comment.user?.nickname || comment.user?.username || '用户'}</strong>{comment.is_public === false && <Tag>私密讨论</Tag>}{showRecruitment && comment.recruitment_application && <Tag color={applicationColors[comment.recruitment_application.status] || 'default'}>{applicationLabels[comment.recruitment_application.status]}</Tag>}<time>{comment.created_at && new Date(comment.created_at).toLocaleString('zh-CN')}</time>{String(targetId) === String(comment.id) && <span className="commentThreads-target-label">当前定位</span>}</div>
      {parentComment && <span className="commentThreads-parent">回复 {parentComment.user?.nickname || parentComment.user?.username || '用户'}</span>}
      <p>{comment.content}</p>
      <div className="commentThreads-actions">
        <Button type="text" size="small" disabled={comment.depth >= 32} onClick={() => onReply(comment)}>{comment.depth >= 32 ? '暂不支持继续回复' : '回复'}</Button>
        <ReportButton targetType="comment" targetId={comment.id} authorId={comment.user?.id} />
        {children}
      </div>
      {renderReply?.(comment)}
    </div>
  </article>;
};

const CommentThread = ({ comment, postId, onReply, onComments, newComment, focusPath = [], targetId, showRecruitment, renderReply }) => {
  const [open, setOpen] = useState(false);
  const [replies, setReplies] = useState([]);
  const [visibleCount, setVisibleCount] = useState(REPLY_BATCH_SIZE);
  const [revealedId, setRevealedId] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const [replyTotal, setReplyTotal] = useState(0);
  const nextPage = useRef(0);
  const oldestLoaded = useRef(false); const seenNewReply = useRef(null);
  const loadLock = useRef(false);
  const rootId = String(comment.id);
  const focusedReplies = String(focusPath[0]?.id) === rootId ? focusPath.slice(1) : [];
  const ownNewReply = newComment?.parent_id && (String(newComment.root_id) === rootId || String(newComment.parent_id) === rootId) ? newComment : null;
  const allReplies = sortReplies(mergeComments(mergeComments(replies, focusedReplies), ownNewReply ? [ownNewReply] : []));
  const commentsById = new Map([comment, ...allReplies].map(item => [String(item.id), item]));
  const revealIndex = revealedId ? allReplies.findIndex(item => String(item.id) === String(revealedId)) : -1;
  const shownCount = Math.max(visibleCount, Math.ceil((revealIndex + 1) / REPLY_BATCH_SIZE) * REPLY_BATCH_SIZE);
  const shouldReveal = focusedReplies.length > 0 || Boolean(ownNewReply);

  useEffect(() => {
    if (shouldReveal) {
      setOpen(true);
      setRevealedId(ownNewReply?.id || targetId);
    }
  }, [shouldReveal, targetId, ownNewReply]);

  useEffect(() => {
    if (ownNewReply && seenNewReply.current !== ownNewReply.id) {
      seenNewReply.current = ownNewReply.id;
      setReplyTotal(n => n + 1);
      setReplies(current => mergeComments(current, [ownNewReply]));
    }
  }, [ownNewReply]);

  useEffect(() => {
    let active = true;
    setLoading(true); setError('');
    fetchPostComments(postId, { root_id: comment.id, page: 1, pageSize: 1 })
      .then(raw => {
        const result = commentPage(raw);
        if (!active) return;
        setReplyTotal(Math.max(0, (result.pagination.total ?? result.data.length) - 1));
        nextPage.current = Array.isArray(raw) || result.pagination.total <= 1 ? 0 : Math.ceil(result.pagination.total / 50);
        oldestLoaded.current = !nextPage.current;
        if (!nextPage.current) {
          setReplies(current => mergeComments(result.data.filter(item => String(item.id) !== String(comment.id)), current));
          onComments?.(result.data);
        }
        setLoaded(true);
      }).catch(err => { if (active) setError(getPostErrorMessage(err, '回复加载失败')); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [postId, comment.id, retry, onComments]);

  const loadNextReplies = async () => {
    if (loadLock.current || !nextPage.current) return;
    loadLock.current = true; setLoading(true); setError('');
    try {
      // 后端倒序分页，从末页向前读取，展示时统一时间正序，只缩进一次。
      let collected = [];
      do {
        const result = commentPage(await fetchPostComments(postId, { root_id: comment.id, page: nextPage.current, pageSize: 50 }));
        collected = mergeComments(collected, result.data.filter(item => String(item.id) !== rootId));
        nextPage.current -= 1;
      } while (nextPage.current > 0 && collected.length < REPLY_BATCH_SIZE);
      oldestLoaded.current = true;
      setReplies(current => mergeComments(current, collected)); onComments?.(collected);
    } catch (err) { setError(getPostErrorMessage(err, '回复加载失败')); }
    finally { loadLock.current = false; setLoading(false); }
  };
  useEffect(() => {
    if (open && loaded && !loading && nextPage.current && !oldestLoaded.current && !error) loadNextReplies();
    // 加载函数只在首次展开时启动；其余批次由用户主动展开。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, loaded, loading, replies.length, error]);
  const total = Math.max(replyTotal, allReplies.length);

  return <div className="commentThreads-branch">
    <CommentRow {...{ comment, onReply, targetId, showRecruitment, renderReply }}>
      {(total > 0 || (!loaded && comment.reply_count > 0)) && <Button type="text" size="small" disabled={loading && !loaded}
        onClick={() => { setOpen(value => !value); setVisibleCount(REPLY_BATCH_SIZE); setRevealedId(null); }}>
        {open ? '收起回复' : loaded ? `展开 ${total} 条回复` : '正在加载回复…'}
      </Button>}
    </CommentRow>
    {error && <Alert type="warning" showIcon title={error} action={<Button size="small" onClick={() => setRetry(value => value + 1)}>重试</Button>} />}
    {open && <div className="commentThreads-children">
      {allReplies.slice(0, shownCount).map(reply => <CommentRow key={reply.id} comment={reply}
        parentComment={commentsById.get(String(reply.parent_id)) || comment} {...{ onReply, targetId, showRecruitment, renderReply }} />)}
      {(shownCount < allReplies.length || nextPage.current > 0) && <Button type="text" size="small" loading={loading} onClick={async () => { if (shownCount + REPLY_BATCH_SIZE > allReplies.length) await loadNextReplies(); setVisibleCount(shownCount + REPLY_BATCH_SIZE); }}>
        展开更多回复（剩余 {Math.max(0, total - shownCount)} 条）
      </Button>}
    </div>}
  </div>;
};
const CommentThreads = ({ comments, ...props }) => <div className="commentThreads">{comments.map(comment => <CommentThread key={comment.id} comment={comment} {...props} />)}</div>;
export default CommentThreads;
